/**
 * <s-newsletter-popup> — timed / exit-intent signup popup.
 *
 * Opens its <dialog> once per visit after `data-delay` ms, or earlier when the pointer
 * leaves through the top of the viewport (desktop exit intent). Closing it (button,
 * Escape or backdrop) snoozes it for `data-snooze-days`. A successful signup
 * (`data-success`) re-opens the dialog to show the confirmation and snoozes for a year.
 */
const STORAGE_KEY = 'sekret:newsletter-popup-until';
const DAY_MS = 24 * 60 * 60 * 1000;

class SNewsletterPopup extends HTMLElement {
  #dialog = null;
  #ac = null;
  #timer = null;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.#dialog.addEventListener('close', () => this.#snooze(this.#snoozeDays()), { signal });
    this.#dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.#dialog) this.#dialog.close();
      },
      { signal }
    );

    if (this.hasAttribute('data-success')) {
      this.#snooze(365);
      this.#open();
      return;
    }

    if (this.#isSnoozed()) return;

    const delay = Number(this.dataset.delay) || 15000;
    this.#timer = window.setTimeout(() => this.#open(), delay);

    document.addEventListener(
      'mouseout',
      (event) => {
        if (!event.relatedTarget && event.clientY <= 0) this.#open();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
    window.clearTimeout(this.#timer);
  }

  #snoozeDays() {
    return this.hasAttribute('data-success') ? 365 : Number(this.dataset.snoozeDays) || 7;
  }

  #isSnoozed() {
    try {
      return Number(localStorage.getItem(STORAGE_KEY) || 0) > Date.now();
    } catch {
      return false;
    }
  }

  #snooze(days) {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now() + days * DAY_MS));
    } catch {
      /* Storage can be unavailable in private modes; the popup then shows once per page view at most. */
    }
  }

  #open() {
    window.clearTimeout(this.#timer);
    if (!this.#dialog || this.#dialog.open) return;
    if (!this.hasAttribute('data-success') && this.#isSnoozed()) return;
    if (document.querySelector('dialog[open]')) return;
    this.#dialog.showModal();
  }
}

if (!customElements.get('s-newsletter-popup')) {
  customElements.define('s-newsletter-popup', SNewsletterPopup);
}
